import style from './ErpDialogPositions.module.scss';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useEventCallback } from '@mui/material/utils';
import clsx from 'clsx';
import i18next from 'i18next';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import {
  Collapse,
  ICollapseProps,
} from '@work4all/components/lib/components/collapse';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';

import { useDataProvider, usePageZoom } from '@work4all/data';

import { InboundPosition } from '@work4all/models/lib/Classes/InboundPosition.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { EditTable } from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/EditTable';
import { useEditableState } from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/hooks/use-editable-state';
import { EditTableEntry } from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { recalculatePositions } from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/hooks/recalculatePositions';
import { RowSizePicker } from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/row-size-picker/RowSizePicker';
import { settings, useSetting } from '../../../../settings';
import { NOT_EDITABLE_ROWS } from '../consts';
import { useApplyPrices } from '../hooks/use-apply-prices';
import {
  ErpConversionGroupByType,
  ErpObject,
  GroupedPosition,
  OnRefresh,
  SelectedPosition,
} from '../types';

import { ExpandAllBom } from './ExpandAllBom';
import { useColumnSettings } from './use-column-settings';
import { useErpDialogPositionColumns } from './use-erp-dialog-position-columns';
import { updateNonRelevantPositions } from './utils';

type ErpDialogPosition = Position & EditTableEntry;
export interface OnItemsChangedOptions {
  forceEnabled: boolean;
  key?: string;
}

const de = i18next.getFixedT('de');

type ConvertPosition = Position & {
  selected?: boolean;
};

export interface ErpDialogPositionsProps extends ICollapseProps {
  commonCollapseProps?: ICollapseProps;
  entry: ErpObject;
  sourceEntity: Entities;
  targetEntity: Entities;
  onItemsChanged: (
    positions: SelectedPosition[],
    options: OnItemsChangedOptions
  ) => void;

  showTableHeader?: boolean;
  historicalItems?: boolean;
  showBOM?: boolean;
  maximizedMask?: boolean;
  autoOpen?: boolean;
  groupByType?: ErpConversionGroupByType;
  positionPatch: SelectedPosition[];
  forceFetchAll?: boolean;
  onRefresh: OnRefresh;
  isEditMode: boolean;
}

export const ErpDialogPositions = (props: ErpDialogPositionsProps) => {
  const {
    maximizedMask,
    historicalItems,
    showBOM,
    sourceEntity,
    targetEntity,
    showTableHeader,
    autoOpen,
    entry,
    groupByType,
    forceFetchAll,
    onRefresh,
    isEditMode,
    ...collapseProps
  } = props;

  // special cases
  const orderToIncomingDeliveryNote =
    sourceEntity === Entities.order &&
    targetEntity === Entities.inboundDeliveryNote;

  const contractToDeliveryNote =
    orderToIncomingDeliveryNote ||
    (sourceEntity === Entities.contract &&
      targetEntity === Entities.deliveryNote);

  const firstTimeLoaded = useRef(false);

  const erpQuantitySuggestion = useSetting(
    settings.erpConvertQuantitySuggestion()
  );

  const { zoomLevel } = usePageZoom();

  const { t } = useTranslation();
  const convertDialogPositionMaximized = useSetting(
    settings.convertDialogPositionMaximized()
  );
  const [maximized, setMaximized] = useState(
    autoOpen ? convertDialogPositionMaximized.value : false
  );

  const requestData = useMemo<DataRequest>(() => {
    return {
      entity: sourceEntity,
      data: {
        id: null,
        currency: {
          id: null,
          name: null,
        },
        positionList: [
          {
            ...(sourceEntity === Entities.inboundDeliveryNote
              ? INBOUND_POSITION_FIELDS
              : POSITION_FIELDS),
          },
        ],
      },
      filter: [{ id: { $eq: entry?.id } }],
    };
  }, [entry, sourceEntity]);

  const { data, loading, refetch } = useDataProvider<ErpObject>(
    requestData,
    (!maximized || firstTimeLoaded.current) && !forceFetchAll
  );

  const [positionList, setPositionList] = useState<Position[]>([]);
  const originalData = useRef<Position[]>(null);

  const mutateState = useCallback((input: Position[]) => {
    const { result } = recalculatePositions(input, false);
    return result;
  }, []);

  const { positions, onEditPosition } = useEditableState<ConvertPosition>({
    positions: positionList,
    mutateState,
  });

  const updatePositionList = useCallback(
    (positionList?: ErpDialogPosition[], quantitySuggestion?: boolean) => {
      const newPositionList = positionList
        .filter((x) =>
          orderToIncomingDeliveryNote || groupByType === 'CONTRACT'
            ? // if order to incoming delivery note, remove all positions headers
              x.positionKind !== ErpPositionsKind.STUECKLISTE
            : contractToDeliveryNote && showBOM
            ? true
            : !x.posId
        )
        .filter(
          (x) =>
            historicalItems ||
            (!historicalItems &&
              x.positionKind !== ErpPositionsKind.UMWANDLUNGSHISTORIE)
        )
        .map((x) =>
          groupByType === 'CONTRACT'
            ? {
                ...x,
                should: x.amount,
              }
            : orderToIncomingDeliveryNote || contractToDeliveryNote
            ? {
                ...x,
                should: x.amount,
                amount: quantitySuggestion ? x.amount : x.backlog,
              }
            : x
        );
      if (historicalItems) {
        newPositionList.unshift({
          id: -1,
          longtext: de('COMMON.ERP.HISTORY_POSITION', {
            entity: de(`COMMON.${sourceEntity.toUpperCase()}`),
            number:
              sourceEntity === Entities.contract
                ? entry.contractNumber
                : entry.number,
            date: DateTime.fromISO(
              sourceEntity === Entities.contract
                ? entry.contractDate
                : entry.date
            ).toLocaleString(DateTimeCustom.DATE_SHORT),
          }),
          amount: 0,
          should: 0,
          positionKind: ErpPositionsKind.UMWANDLUNGSHISTORIE,
          disableSelect: true,
        });
      }
      setPositionList(newPositionList);
    },
    [
      contractToDeliveryNote,
      entry.contractDate,
      entry.contractNumber,
      entry.date,
      entry.number,
      groupByType,
      historicalItems,
      orderToIncomingDeliveryNote,
      showBOM,
      sourceEntity,
    ]
  );

  useEffect(() => {
    if (originalData.current?.length)
      updatePositionList(
        originalData.current,
        erpQuantitySuggestion.value === 'SHOULD'
      );
  }, [historicalItems, updatePositionList, erpQuantitySuggestion.value]);

  const [fetchedData, setFetchData] = useState(() => data);
  useEffect(() => {
    if (data.length) setFetchData(data);
  }, [data]);

  useEffect(() => {
    if (fetchedData[0]?.positionList.length) {
      originalData.current = fetchedData[0]?.positionList;
      updatePositionList(
        fetchedData[0]?.positionList,
        erpQuantitySuggestion.value === 'SHOULD'
      );
    }
  }, [erpQuantitySuggestion.value, fetchedData, updatePositionList]);

  // Update list when changing amount
  useEffect(() => {
    if (contractToDeliveryNote || groupByType === 'CONTRACT') {
      updateSelected(() => true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  const updateSelected = useEventCallback(
    (isSelected: (id: string) => boolean) => {
      const selectedPositions: SelectedPosition[] = [];
      const removedParents: number[] = [];
      const original = originalData.current || [];
      original.forEach((originalPos) => {
        const currentPos = positions.find((x) => x.id === originalPos.id);

        if (!currentPos) {
          if (removedParents.includes(originalPos.posId))
            selectedPositions.push({
              position: originalPos,
              selected: false,
            });
          return;
        }

        const selected = isSelected(currentPos.localId);
        if (
          originalPos.positionKind === ErpPositionsKind.STUECKLISTE &&
          !selected
        ) {
          removedParents.push(originalPos.id);
        }

        selectedPositions.push({
          position: currentPos,
          selected:
            (currentPos.positionKind === ErpPositionsKind.STANDARD ||
              currentPos.positionKind === ErpPositionsKind.STUECKLISTE) &&
            currentPos.amount === 0
              ? false
              : selected,
          newAmount: currentPos.amount,
          newDiscount: currentPos.discount,
          newSinglePrice: currentPos.singlePriceNet,
          newUnit: currentPos.unit,

          oldAmount: originalPos.amount,
          oldDiscount: originalPos.discount,
          oldSinglePrice: originalPos.singlePriceNet,
          oldUnit: currentPos.unit,
        });
      });

      if (contractToDeliveryNote) {
        const updatedPositions = updateNonRelevantPositions(selectedPositions);
        props.onItemsChanged(updatedPositions, { forceEnabled: false });
      } else {
        props.onItemsChanged(selectedPositions, { forceEnabled: false });
      }
    }
  );

  const columns = useErpDialogPositionColumns({
    contractToDeliveryNote,
    data,
    onEditPosition,
    groupByType,
    onRefresh: forceFetchAll ? onRefresh : undefined,
    isEditMode,
    onRefetch: refetch,
  });

  const decorators = useMemo(() => {
    if (!(contractToDeliveryNote && showBOM)) return undefined;
    return {
      getRelation: (position: Position) => {
        if (position.posId) return 'child';
        return 'normal';
      },
    };
  }, [contractToDeliveryNote, showBOM]);

  const tableInstanceRef = useRef<TableInstance>(null);
  useEffect(() => {
    if (
      tableInstanceRef.current &&
      positionList.length &&
      !loading &&
      !firstTimeLoaded.current
    ) {
      firstTimeLoaded.current = true;
    }
  }, [loading, positionList.length]);

  const onSelectedItemIdsChange = useEventCallback((items: string[]) => {
    if (contractToDeliveryNote) return;
    updateSelected((localId) => items.includes(localId));
  });

  const erpSize = useSetting(settings.erpRowSize());

  const allowedColumns = useMemo(() => {
    return columns.map((x) => x.accessor as string);
  }, [columns]);

  const selectedRowIds = useMemo(() => {
    return positions.reduce((prev, cur) => {
      prev[cur.localId] = true;
      return prev;
    }, {} as Record<string, boolean>);
  }, [positions]);

  const { columnSettings, element: resetColumnElement } = useColumnSettings(
    allowedColumns,
    groupByType
  );

  // We need to save values and refresh the view
  useApplyPrices(data, setFetchData, groupByType);

  // Keep selection between tabs
  useEffect(() => {
    const tableInstance = tableInstanceRef.current;
    if (!tableInstance) return;
    return positions.forEach((item) => {
      if (item.selected) {
        tableInstance.toggleRowSelected(item.localId, true);
        item.selected = undefined;
      } else if (item.selected === false) {
        tableInstance.toggleRowSelected(item.localId, false);
        item.selected = undefined;
      }
    });
  }, [positions, tableInstanceRef]);

  useEffect(() => {
    setMaximized(maximizedMask);
  }, [maximizedMask]);

  return (
    <Collapse
      {...collapseProps}
      defaultOpen={data?.[0]?.positionList.length === 0 ? false : maximized}
      open={maximized}
      onToggled={(open) => {
        if (autoOpen) collapseProps.onToggled(open);
        else setMaximized(open);
      }}
      headerBar={
        showTableHeader && (
          <div className={style.collapse}>
            <ExpandAllBom
              onExpandAll={() => {
                collapseProps.onToggled(true);
              }}
              onCloseAll={() => {
                collapseProps.onToggled(false);
              }}
            />
            <div className={style.collapseInner}>
              {contractToDeliveryNote && (
                <>
                  <div
                    className={clsx(style.rowSizePicker, style.positionTitle)}
                  >
                    <Typography>
                      {t('COMMON.ERP.QUANTITY_SUGGESTION')}:
                    </Typography>
                  </div>
                  <div className={style.rowSizePicker}>
                    <ToggleButtonGroup
                      value={erpQuantitySuggestion.value}
                      exclusive
                    >
                      <Tooltip
                        title={t('COMMON.ERP.QUANTITY_SUGGESTION.SHOULD')}
                        placement="top"
                      >
                        <ToggleButton
                          value="SHOULD"
                          onClick={() => erpQuantitySuggestion.set('SHOULD')}
                          className={style.paddingButton}
                        >
                          {t('COMMON.ERP.SHOULD')}
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t('COMMON.ERP.QUANTITY_SUGGESTION.REST')}
                        placement="top"
                      >
                        <ToggleButton
                          value="REST"
                          onClick={() => erpQuantitySuggestion.set('REST')}
                          className={style.paddingButton}
                        >
                          {t('COMMON.ERP.REST')}
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </div>
                  <div className={style.clearButton}>
                    <Button
                      variant="text"
                      onClick={() => {
                        setPositionList((list) => {
                          list.forEach((x) => (x.amount = 0));
                          return cloneDeep(list);
                        });
                      }}
                    >
                      {t('COMMON.CLEAR_UP')}
                    </Button>
                  </div>
                </>
              )}
              {resetColumnElement}
              <div className={style.rowSizePicker}>
                <RowSizePicker value={erpSize.value} onChange={erpSize.set} />
              </div>
            </div>
          </div>
        )
      }
    >
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <EditTable<GroupedPosition>
          tableInstanceRef={tableInstanceRef}
          items={positions}
          columns={columns}
          classes={{
            table: style.table,
            tbody: style.tbody,
            theadSeparatorWrapper: (() => {
              switch (zoomLevel) {
                case 80:
                  return style.theadSeparatorWrapperTiny;
                case 90:
                  return style.theadSeparatorWrapperSmall;
                case 100:
                  return style.theadSeparatorWrapperRegular;
                default:
                  return style.theadSeparatorWrapperLarge;
              }
            })(),
          }}
          initialState={{
            selectedRowIds,
          }}
          decorators={decorators}
          editableRows={NOT_EDITABLE_ROWS}
          showSelectionColumn={!contractToDeliveryNote}
          showSelectionColumnOnInit={!contractToDeliveryNote}
          onSelectedItemIdsChange={onSelectedItemIdsChange}
          rowSizeMode={erpSize.value}
          columnSettings={columnSettings}
          fitSpace
        />
      )}
    </Collapse>
  );
};

const INBOUND_POSITION_FIELDS: InboundPosition = {
  id: null,
  index: null,
  number: null,
  longtext: null,
  positionKind: null,
  amount: null,
  discount: null,
  unit: null,
  singlePriceNet: null,
  totalPriceNet: null,
  articleId: null,
  articleNumber: null,
};

const POSITION_FIELDS: Position = {
  ...INBOUND_POSITION_FIELDS,
  supplierId: null,
  supplier: {
    id: null,
    name: null,
  },
  insteadOfTotalPrice: null,
  backlog: null,
  partsListAmount: null,
  posId: null,
  objectParentId: null,
  purchasePrice: null,
};
